import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import type { ConstructionTypeData, FileInput } from 'gql/graphql';
import { BimChapterEnum, CostGroupEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { IListItem } from 'types/Contract';
import { Button, cnMixSpace } from 'uikit';

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BimUploadPriceFilesModal } from '../../BimCommonComponents/BimUploadPriceFilesModal';
import { BimActKc2OtherEdit } from '../BimActKc2OtherEdit/BimActKc2OtherEdit';
import { useBimUploadActKc2Files } from '../BimUploadPriceFiles/hooks/useBimUploadActKc2Files';
import { useBimWorkOrderObjectsListItems } from '../hooks';
import { isBimChapter } from '../types';

import { BimAddKC2UploadResolver } from './BimAddKC2UploadResolver';

interface IBimAddKC2 {
    withBtn?: boolean;
    selectedChapterOrSection?: IListItem<string>;
    selectedObject?: IListItem<number>;
    workOrderId?: number;
    onUploadClose: (isSuccess: boolean) => void;
    workOrderStatus?: WorkOrderStatusEnum;
    isFrameContract: boolean;
    constructionType?: ConstructionTypeData;
    clickOpenRef?: React.MutableRefObject<any>;
    priceTableApiRef?: React.RefObject<any>;
    refreshProject?: () => void;
    togglePopover: () => void;
    statusProject?: ProjectStatusEnum;
    isDisabledCreateKC2OtherRules?: boolean;
    isDisabledCreateAktKC2BimRules?: boolean;
}

export const BimAddKC2 = (props: IBimAddKC2) => {
    const {
        workOrderStatus,
        withBtn = true,
        selectedChapterOrSection,
        selectedObject,
        workOrderId,
        statusProject,
        constructionType,
        onUploadClose,
        isFrameContract,
        togglePopover,
        priceTableApiRef,
        refreshProject,
        isDisabledCreateKC2OtherRules,
        isDisabledCreateAktKC2BimRules,
    } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: workOrderId,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderId,
        ],
    );

    const {
        bimUploadActKc2FilesData,
        bimUploadActKc2FilesStatus,
        bimUploadActKc2FilesError,
        bimUploadActKc2FilesMakeRequest,
        bimUploadActKc2FilesReset,
        bimUploadActKc2FilesAbortRequest,
    } = useBimUploadActKc2Files();

    const onClose = useCallback(
        (isSuccess: boolean) => {
            setIsModalOpen(false);
            onUploadClose(isSuccess);
            togglePopover();
        },
        [onUploadClose, togglePopover],
    );

    const isOthers = useMemo(
        () => selectedChapterOrSection?.id === BimChapterEnum.Others,
        [selectedChapterOrSection?.id],
    );

    const onOpen = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_add act KC button_pressed',
            ...analyticsData,
        });

        setIsModalOpen(true);
    }, [analyticsData, sendGpnAnalyticsEvent]);

    const bimChapter = useMemo(
        () =>
            isBimChapter(selectedChapterOrSection?.id) ? (selectedChapterOrSection?.id as BimChapterEnum) : undefined,
        [selectedChapterOrSection?.id],
    );

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const toolTipMessage = useMemo(() => {
        if (workOrderStatus !== WorkOrderStatusEnum.InProgress) {
            return `Добавление Акта КС-2 доступно только в статусе ${
                isFrameContract ? 'НЗ' : 'Расчета по Договору'
            } «В работе»`;
        }

        if (!selectedChapterOrSection || !selectedObject) {
            return 'Для создания Акта КС-2 выберите раздел и объект';
        }

        return '';
    }, [isFrameContract, selectedChapterOrSection, selectedObject, workOrderStatus]);

    const { reset: objectsListItemsReset, data: objectsListItems } = useBimWorkOrderObjectsListItems({
        workOrderId,
        rootId: selectedObject?.id,
        requestKey: 'BimWorkOrderUploadPrice',
    });

    const resetObjectsListItems = useCallback(() => {
        objectsListItemsReset(undefined);
    }, [objectsListItemsReset]);

    const successBtnTitle = useMemo(() => `Перейти в ${isFrameContract ? 'Наряд-Заказ' : 'Расчет'}`, [isFrameContract]);

    const makeUploadRequestHandler = useCallback(
        (vars: {
            chapter: BimChapterEnum | undefined;
            filesContent: FileInput[];
            workOrderObjectId: number | undefined;
            hasTotalsFromXml?: boolean;
        }) => {
            const { chapter, filesContent, workOrderObjectId, hasTotalsFromXml = true } = vars;

            if (chapter) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_act KC save button_pressed',
                    ...analyticsData,
                });

                bimUploadActKc2FilesMakeRequest({
                    chapter,
                    filesContent,
                    workOrderObjectId,
                    workOrderId,
                    hasTotalsFromXml,
                });
            }
        },
        [analyticsData, bimUploadActKc2FilesMakeRequest, sendGpnAnalyticsEvent, workOrderId],
    );

    const successBtnHandler = () => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_act KC file_saved',
            ...analyticsData,
            costGroup: CostGroupEnum.Cmr,
        });
    };

    return (
        <>
            {withBtn && (
                <TooltipWrapper direction="rightCenter" message={toolTipMessage} isHidden={!toolTipMessage}>
                    <Button
                        disabled={
                            !!toolTipMessage ||
                            isProjectStatusDelayed ||
                            (isDisabledCreateKC2OtherRules && isOthers) ||
                            (isDisabledCreateAktKC2BimRules && !isOthers)
                        }
                        label="Акт КС-2"
                        size="s"
                        className={cnMixSpace({ mR: 'xs' })}
                        onClick={onOpen}
                        form="brick"
                        width="full"
                        view="clear"
                    />
                </TooltipWrapper>
            )}

            {isModalOpen && !isOthers && (
                <>
                    <BimUploadPriceFilesModal
                        selectedObject={selectedObject}
                        chapter={bimChapter}
                        onClose={onClose}
                        titleModal="Добавление Акта КС-2"
                        textInformer="Акт будет добавлен в состав сметы в соответствии с выбранным объектом строительства и главой расчета. В случае, если Вы загружаете новую смету с фактом, необходимо выбрать подобъект перед загрузкой сметы"
                        uploadData={bimUploadActKc2FilesData}
                        uploadStatus={bimUploadActKc2FilesStatus}
                        uploadError={bimUploadActKc2FilesError}
                        uploadMakeRequest={makeUploadRequestHandler}
                        uploadReset={bimUploadActKc2FilesReset}
                        uploadAbort={bimUploadActKc2FilesAbortRequest}
                        objectsListItems={objectsListItems}
                        successButtonTitle={successBtnTitle}
                        isConfirmDisabled={!workOrderId}
                        outerResetHandler={resetObjectsListItems}
                        refreshProject={refreshProject}
                        successBtnHandler={successBtnHandler}
                        withActFileSource
                    />

                    {bimUploadActKc2FilesData?.data && (
                        <BimAddKC2UploadResolver
                            workOrderId={workOrderId}
                            bimUploadKC2Data={bimUploadActKc2FilesData}
                            bimUploadKC2Status={bimUploadActKc2FilesStatus}
                            bimUploadReset={bimUploadActKc2FilesReset}
                            onClose={onClose}
                        />
                    )}
                </>
            )}

            {isModalOpen && isOthers && (
                <BimActKc2OtherEdit
                    onClose={onClose}
                    workOrderId={workOrderId}
                    constructionType={constructionType}
                    selectedObject={selectedObject}
                    priceTableApiRef={priceTableApiRef}
                    refreshProject={refreshProject}
                />
            )}
        </>
    );
};

BimAddKC2.displayName = 'BimAddKC2';
